import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import FloatingBackButton from './FloatingBackButton';
import type { GrammarCategory, GrammarItem, LanguageCode } from '../types/types';
import { useTheme } from './ThemeContext';
import { normalizeText, calculateSimilarity } from '../utils/sentencesgapUtils';

import { EmojiText, Picto } from './common/Picto';
const SIMILARITY_THRESHOLD = 0.8;
const CLOSE_THRESHOLD = 0.6;
const MAX_ATTEMPTS = 2;

const SPECIAL_CHARS: Partial<Record<LanguageCode, string[]>> = {
  ba: ['ɛ', 'ɔ', 'ɲ', 'ŋ', 'ɓ', 'ɗ', 'ɛ̀', 'ɔ̀'],
  wf: ['à', 'â', 'ñ', 'ŋ', 'ó'],
  ff: ['ɓ', 'ɗ', 'ƴ', 'ŋ', 'ɲ', 'ɔ', 'ɛ', 'ƥ'],
  la: ['ɔ', 'ɛ'],
  sw: [],
  co: [],
  yo: ['ẹ', 'ọ', 'ṣ', 'á', 'à', 'é', 'è', 'í', 'ì', 'ó', 'ò', 'ú', 'ù', 'ń'],
};

interface EvalSentence {
  type: 'gap' | 'direct';
  bambara: string;
  french: string;
  testWord: string;
  ruleKey: string;
  ruleTranslation: string;
  subcategory: string;
}

export interface GrammarModuleEvalProps {
  grammarCategories: GrammarCategory;
  completedRules: Set<string>;
  unitName: string;
  languageCode: LanguageCode;
  onComplete: () => void;
  onBack: () => void;
  isMobileView?: boolean;
}

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function parseExample(example: string): { bambara: string; french: string } | null {
  const firstPart = example.split(' / ')[0].trim();
  const match = firstPart.match(/^(.*?)\s*\((.+)\)\s*$/);
  if (!match) return null;
  return {
    bambara: match[1].trim(),
    french: match[2].trim(),
  };
}

function getTestWord(key: string): string {
  return key.replace(/\s*\([^)]+\)$/, '').trim();
}

function generateEvalSentences(
  grammarCategories: GrammarCategory,
  completedRules: Set<string>
): EvalSentence[] {
  const sentences: EvalSentence[] = [];

  for (const [subcategory, rules] of Object.entries(grammarCategories)) {
    for (const [key, value] of Object.entries(rules as Record<string, GrammarItem>)) {
      if (key.startsWith('_')) continue;
      if (!completedRules.has(key)) continue;

      const item = value as GrammarItem;
      const testWord = getTestWord(key);
      if (!testWord) continue;

      const translation = item.translation || item.fr || '';

      // Try gap exercise first
      const exampleStr = item.example || (item.examples && item.examples[0]);
      let addedAsGap = false;

      if (exampleStr) {
        const parsed = parseExample(exampleStr);
        if (parsed?.bambara && parsed?.french) {
          const escaped = testWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          try {
            if (new RegExp(escaped, 'i').test(parsed.bambara)) {
              sentences.push({
                type: 'gap',
                bambara: parsed.bambara,
                french: parsed.french,
                testWord,
                ruleKey: key,
                ruleTranslation: translation,
                subcategory,
              });
              addedAsGap = true;
            }
          } catch { /* skip */ }
        }
      }

      // Fallback: direct translation exercise for all words not covered by gap
      if (!addedAsGap && translation) {
        sentences.push({
          type: 'direct',
          bambara: testWord,
          french: translation,
          testWord,
          ruleKey: key,
          ruleTranslation: translation,
          subcategory,
        });
      }
    }
  }

  return shuffleArray(sentences);
}

const GrammarModuleEval: React.FC<GrammarModuleEvalProps> = ({
  grammarCategories,
  completedRules,
  unitName,
  languageCode,
  onComplete,
  onBack,
  isMobileView = false,
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isDark = theme === 'dark';
  const inputRef = useRef<HTMLInputElement>(null);

  const sentences = useMemo(
    () => generateEvalSentences(grammarCategories, completedRules),
    []
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState<{
    type: 'correct' | 'close' | 'wrong' | 'revealed';
    message: string;
  } | null>(null);
  const [answered, setAnswered] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [done, setDone] = useState(false);
  const [resultRows, setResultRows] = useState<{ sentence: EvalSentence; wasCorrect: boolean }[]>([]);

  const currentSentence = sentences[currentIndex];
  const isLast = currentIndex === sentences.length - 1;

  const displayText = useMemo(() => {
    if (!currentSentence) return '';
    if (currentSentence.type === 'direct') return '_____';
    const escaped = currentSentence.testWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return currentSentence.bambara.replace(new RegExp(escaped, 'i'), '_____');
  }, [currentSentence]);

  const feedbackColors = {
    correct:  { bg: 'var(--color-success-tint)', text: 'var(--color-success-light)' },
    close:    { bg: 'var(--color-warning-tint)', text: 'var(--gold-primary)' },
    wrong:    { bg: 'var(--color-error-tint)', text: 'var(--color-error)' },
    revealed: { bg: 'var(--color-error-tint)', text: 'var(--color-error)' },
  };

  const insertChar = useCallback((char: string) => {
    const input = inputRef.current;
    if (!input) return;
    const start = input.selectionStart ?? userInput.length;
    const end = input.selectionEnd ?? userInput.length;
    const next = userInput.slice(0, start) + char + userInput.slice(end);
    setUserInput(next);
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(start + char.length, start + char.length);
    }, 0);
  }, [userInput]);

  const handleSubmit = useCallback(() => {
    if (!currentSentence || answered || !userInput.trim()) return;

    const correct = currentSentence.testWord;
    const norm = normalizeText(userInput.trim());
    const normCorrect = normalizeText(correct);
    const similarity = calculateSimilarity(norm, normCorrect);
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);

    if (similarity >= SIMILARITY_THRESHOLD) {
      setFeedback({ type: 'correct', message: t('grammarModuleEval.correct', { answer: correct }) });
      setAnswered(true);
      setCorrectCount(c => c + 1);
      setResultRows(r => [...r, { sentence: currentSentence, wasCorrect: true }]);
    } else if (similarity >= CLOSE_THRESHOLD) {
      setFeedback({ type: 'close', message: t('grammarModuleEval.close', { answer: correct }) });
      setAnswered(true);
      setResultRows(r => [...r, { sentence: currentSentence, wasCorrect: false }]);
    } else if (newAttempts >= MAX_ATTEMPTS) {
      setFeedback({ type: 'revealed', message: t('grammarModuleEval.revealed', { answer: correct }) });
      setAnswered(true);
      setResultRows(r => [...r, { sentence: currentSentence, wasCorrect: false }]);
    } else {
      setFeedback({ type: 'wrong', message: t('grammarModuleEval.wrong') });
      setUserInput('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [currentSentence, userInput, attempts, answered, t]);

  const handleNext = useCallback(() => {
    if (isLast) {
      setDone(true);
    } else {
      setCurrentIndex(i => i + 1);
      setUserInput('');
      setAttempts(0);
      setFeedback(null);
      setAnswered(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isLast]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (answered) handleNext();
      else handleSubmit();
    }
  };

  if (sentences.length === 0) {
    return (
      <div className="component-wrapper">
        <div className="grammar-main-container card" data-theme={theme}>
          <div className="grammar-header">
            <FloatingBackButton onBack={onBack} mode="inline" variant="ghost" text={t('header.back')} />
          </div>
          <div style={{ textAlign: 'center', padding: '40px 20px', color: isDark ? '#b8a88c' : '#666' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}><Picto name="livre" /></div>
            <p style={{ fontWeight: 600, marginBottom: '8px', color: isDark ? '#e8d5b0' : '#2c1810' }}>
              {t('grammarModuleEval.noRuleCompleted')}
            </p>
            <p style={{ fontSize: '0.85rem' }}>
              {t('grammarModuleEval.learnRulesFirst')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    const score = correctCount;
    const total = sentences.length;
    const pct = Math.round((score / total) * 100);
    const medal = pct >= 80 ? '🥇' : pct >= 60 ? '🥈' : '🥉';
    const message =
      pct >= 80 ? t('grammarModuleEval.resultExcellent') :
      pct >= 60 ? t('grammarModuleEval.resultGood') :
      t('grammarModuleEval.resultKeepPracticing');

    return (
      <div className="component-wrapper">
        <div className="grammar-main-container card" data-theme={theme}>
          <div className="grammar-header">
            <FloatingBackButton onBack={onBack} mode="inline" variant="ghost" text={t('header.back')} />
          </div>
          <div className="grammar-level-card">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
              style={{ textAlign: 'center', marginBottom: '24px' }}
            >
              <div style={{ fontSize: '4rem', marginBottom: '8px' }}><EmojiText text={medal} /></div>
              <h2 style={{ color: 'var(--gold-primary)', margin: '0 0 4px', fontSize: '1.4rem' }}>
                {t('grammarModuleEval.evalFinished')}
              </h2>
              <p style={{ color: isDark ? '#b8a88c' : '#666', margin: 0, fontSize: '0.9rem' }}>
                {unitName}
              </p>
            </motion.div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', marginBottom: '20px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--gold-primary)' }}>
                  {score}/{total}
                </div>
                <div style={{
                  fontSize: '0.72rem',
                  color: isDark ? '#b8a88c' : '#888',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginTop: '2px',
                }}>
                  {t('grammarModuleEval.score')}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: pct >= 80 ? 'var(--color-success-light)' : pct >= 60 ? 'var(--gold-primary)' : 'var(--color-error)',
                }}>
                  {pct}%
                </div>
                <div style={{
                  fontSize: '0.72rem',
                  color: isDark ? '#b8a88c' : '#888',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginTop: '2px',
                }}>
                  {t('grammarModuleEval.successRate')}
                </div>
              </div>
            </div>

            <p style={{
              textAlign: 'center',
              color: isDark ? '#e8d5b0' : '#2c1810',
              fontWeight: 600,
              marginBottom: '20px',
            }}>
              {message}
            </p>

            <div style={{ marginBottom: '20px', maxHeight: '260px', overflowY: 'auto' }}>
              {resultRows.map((row, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    marginBottom: '6px',
                    backgroundColor: row.wasCorrect
                      ? (isDark ? 'rgba(52,211,153,0.1)' : 'rgba(52,211,153,0.08)')
                      : (isDark ? 'rgba(248,113,113,0.1)' : 'rgba(248,113,113,0.08)'),
                  }}
                >
                  <span style={{ fontWeight: 600, color: isDark ? '#e8d5b0' : '#2c1810', fontSize: '0.95rem' }}>
                    {row.sentence.testWord}
                  </span>
                  <span style={{ color: isDark ? '#b8a88c' : '#666', fontSize: '0.85rem', flex: 1, textAlign: 'center', padding: '0 8px' }}>
                    {row.sentence.ruleTranslation}
                  </span>
                  {row.wasCorrect
                    ? <CheckCircle size={16} color="var(--color-success-light)" />
                    : <XCircle size={16} color="var(--color-error)" />}
                </div>
              ))}
            </div>

            <motion.button
              onClick={onComplete}
              className="grammar-action-button primary"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              {t('grammarModuleEval.backToModule')}
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="component-wrapper">
      <div className="grammar-main-container card" data-theme={theme}>
        <div className="grammar-header">
          <FloatingBackButton onBack={onBack} mode="inline" variant="ghost" text={t('header.back')} />
          {!isMobileView && (
            <h2 style={{ color: 'var(--gold-primary)', margin: 0, fontSize: '1rem' }}>
              {t('grammarModuleEval.evalTitle', { unitName })}
            </h2>
          )}
        </div>

        <div className="grammar-level-card">
          <div className="preview-progress">
            <p style={{ margin: '0 0 6px', fontSize: '0.85rem', color: isDark ? '#b8a88c' : '#666' }}>
              {t('grammarModuleEval.questionCounter', { current: currentIndex + 1, total: sentences.length })}
            </p>
            <div className="grammar-progress-bar">
              <div
                className="grammar-progress-fill"
                style={{ width: `${((currentIndex + 1) / sentences.length) * 100}%` }}
              />
            </div>
          </div>

          <div style={{
            display: 'inline-block',
            padding: '4px 14px',
            borderRadius: '20px',
            backgroundColor: isDark ? '#2a2621' : '#fdf3dc',
            border: '1px solid var(--gold-primary)',
            marginBottom: '16px',
            fontSize: '0.82rem',
            color: 'var(--gold-primary)',
          }}>
            {currentSentence.subcategory}
          </div>

          <div style={{
            padding: '20px',
            backgroundColor: isDark ? '#1e1a17' : '#fafafa',
            borderRadius: '12px',
            border: `1px solid ${isDark ? '#3a3530' : '#eeeeee'}`,
            marginBottom: '16px',
          }}>
            {currentSentence.type === 'direct' ? (
              <>
                <p style={{
                  fontSize: '0.78rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: isDark ? '#b8a88c' : '#888',
                  marginBottom: '6px',
                  margin: '0 0 6px',
                }}>
                  {t('grammarModuleEval.howToSayInBambara')}
                </p>
                <p style={{
                  fontSize: '1.35rem',
                  fontWeight: 700,
                  color: isDark ? '#e8d5b0' : '#2c1810',
                  margin: '0 0 4px',
                }}>
                  {currentSentence.french}
                </p>
                <p style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: isDark ? '#c9a961' : '#8b6914',
                  letterSpacing: '0.04em',
                  margin: 0,
                }}>
                  {displayText}
                </p>
              </>
            ) : (
              <>
                <p style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: isDark ? '#e8d5b0' : '#2c1810',
                  marginBottom: '8px',
                  lineHeight: 1.6,
                  letterSpacing: '0.01em',
                }}>
                  {displayText}
                </p>
                <p style={{
                  fontSize: '0.9rem',
                  color: isDark ? '#b8a88c' : '#666',
                  fontStyle: 'italic',
                  margin: 0,
                }}>
                  {currentSentence.french}
                </p>
              </>
            )}
          </div>

          {!answered && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input
                  ref={inputRef}
                  type="text"
                  value={userInput}
                  onChange={e => setUserInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('grammarModuleEval.completePlaceholder')}
                  autoFocus
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: `1.5px solid ${isDark ? '#3a3530' : '#ddd'}`,
                    backgroundColor: isDark ? '#2a2621' : '#fff',
                    color: isDark ? '#e8d5b0' : '#2c1810',
                    fontSize: '1rem',
                    outline: 'none',
                    fontFamily: 'inherit',
                  }}
                />
                <motion.button
                  onClick={handleSubmit}
                  className="grammar-action-button primary"
                  style={{ flexShrink: 0, padding: '12px 20px' }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={!userInput.trim()}
                >
                  {t('grammarModuleEval.verify')}
                </motion.button>
              </div>

              {/* Clavier de caractères spéciaux */}
              {(SPECIAL_CHARS[languageCode] ?? []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {(SPECIAL_CHARS[languageCode] ?? []).map(char => (
                    <motion.button
                      key={char}
                      onMouseDown={e => { e.preventDefault(); insertChar(char); }}
                      whileHover={{ scale: 1.15 }}
                      whileTap={{ scale: 0.9 }}
                      style={{
                        padding: '5px 11px',
                        borderRadius: '8px',
                        border: `1px solid ${isDark ? '#3a3530' : '#ddd'}`,
                        backgroundColor: isDark ? '#2a2621' : '#f5f0e8',
                        color: isDark ? '#e8d5b0' : '#2c1810',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                        lineHeight: 1.4,
                        fontWeight: 500,
                      }}
                    >
                      {char}
                    </motion.button>
                  ))}
                </div>
              )}
            </div>
          )}

          <AnimatePresence>
            {feedback && (
              <motion.div
                key="feedback"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                style={{
                  padding: '14px 16px',
                  borderRadius: '10px',
                  marginBottom: '16px',
                  backgroundColor: feedbackColors[feedback.type].bg,
                  color: feedbackColors[feedback.type].text,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                }}
              >
                {feedback.type === 'correct' && <CheckCircle size={18} />}
                {(feedback.type === 'close' || feedback.type === 'revealed') && <HelpCircle size={18} />}
                {feedback.type === 'wrong' && <XCircle size={18} />}
                {feedback.message}
              </motion.div>
            )}
          </AnimatePresence>

          {answered && (
            <motion.button
              onClick={handleNext}
              className="grammar-action-button primary"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isLast ? t('grammarModuleEval.seeResults') : t('grammarModuleEval.nextQuestion')}
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GrammarModuleEval;
